import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom"; // Import useLocation
import LeftNav from "../../common/leftnav";
import UploadButton from "./upload_btn/upload_btn";
import EditDeleteButton from "./file_manage/edit_delete";
import authService from "../../../services/authService";
import { useUser } from "../../../context/UserContext";

const KnowledgeBase = () => {
  const location = useLocation(); // Use useLocation to track route changes
  const navigate = useNavigate();
  const { token } = useUser(); // Extract token from useUser context
  const [showModal, setShowModal] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [fileNames, setFileNames] = useState([
    { name: "Untitled", timestamp: new Date().toLocaleString() },
  ]);
  const [selectedFile, setSelectedFile] = useState(fileNames[0]);
  const [hoveredFile, setHoveredFile] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [webUrls, setWebUrls] = useState([]);
  const [textFiles, setTextFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [editUrlIndex, setEditUrlIndex] = useState(null);
  const [editTextIndex, setEditTextIndex] = useState(null);
  const [editFileIndex, setEditFileIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // State to store error message
  const [isDeleting, setIsDeleting] = useState(false); // State to track deletion progress
  const [isLoading, setIsLoading] = useState(false); // State to track general loading
  const [isSaving, setIsSaving] = useState(false); // State to track saving progress
  const [isContentLoading, setIsContentLoading] = useState(false); // State to track content loading
  const [isSyncing, setIsSyncing] = useState(false); // State to track syncing status
  // Removed unused `loading` state

  const fetchKnowledgeBases = useCallback(async () => {
    try {
      setIsLoading(true); // Show spinner while fetching
      const data = await authService.getKnowledgeBases(token); // Fetch knowledge bases from API
      if (!data || data.length === 0) {
        // Ensure only one "Untitled" file is created
        const untitledFile = {
          name: "Untitled",
          timestamp: new Date().toLocaleString(), // Set current timestamp
          webUrls: [],
          textFiles: [],
          uploadedFiles: [],
        };

        try {
          const response = await authService.createKnowledgeBase(
            { name: "Untitled" }, // Only send the name to the API
            token
          ); // Create "Untitled" in the database
          untitledFile._id = response._id; // Assign the database ID
        } catch (error) {
          console.error(
            "Failed to create default Untitled knowledge base:",
            error
          );
        }

        setFileNames([untitledFile]);
        setSelectedFile(untitledFile);
        setWebUrls([]);
        setTextFiles([]);
        setUploadedFiles([]);
      } else {
        const updatedData = data
          .filter(
            (file, index, self) =>
              file.name !== "Untitled" || // Keep only one "Untitled" file
              self.findIndex((f) => f.name === "Untitled") === index
          )
          .map((file) => ({
            ...file,
            timestamp: file.timestamp || new Date().toLocaleString(), // Ensure timestamp is set
          }));
        setFileNames(updatedData);
        setSelectedFile(updatedData[0]);
        setWebUrls(updatedData[0].webUrls || []);
        setTextFiles(updatedData[0].textFiles || []);
        setUploadedFiles(updatedData[0].uploadedFiles || []);
      }
    } catch (error) {
      console.error("Failed to fetch knowledge bases:", error);
    } finally {
      setIsLoading(false); // Hide spinner after fetching
    }
  }, [token]); // Add 'token' as a dependency

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]); // Add 'fetchKnowledgeBases' to the dependency array

  const handleShowModal = () => {
    setIsRenaming(false);
    setInputValue("");
    setShowModal(true);
  };
  const handleShowRenameModal = () => {
    setIsRenaming(true);
    setInputValue(selectedFile.name);
    setShowModal(true);
  };
  const handleCloseModal = () => setShowModal(false);

  const handleInputChange = (e) => {
    if (e.target.value.length > 20) {
      setErrorMessage("File name cannot exceed 20 characters."); // Set error message if limit is exceeded
    } else {
      setErrorMessage(""); // Clear error message if within limit
      setInputValue(e.target.value);
    }
  };
  const handleSave = async () => {
    if (inputValue.trim()) {
      setIsSaving(true); // Show spinner while saving
      const isDuplicate = fileNames.some(
        (file) => file.name.toLowerCase() === inputValue.trim().toLowerCase()
      );
      if (isDuplicate) {
        setErrorMessage("A file with this name already exists.");
        setIsSaving(false); // Hide spinner after saving
        return;
      }

      const newFile = {
        name: inputValue.trim(),
      };

      try {
        const response = await authService.createKnowledgeBase(newFile, token); // Call API to create knowledge base
        const updatedFile = {
          name: response.name, // Use the name from the API response
          timestamp: new Date(response.createdAt).toLocaleString(), // Use the createdAt timestamp
          webUrls: [],
          textFiles: [],
          uploadedFiles: [],
        };
        setFileNames([...fileNames, updatedFile]);
        setSelectedFile(updatedFile);
        setInputValue("");
        setErrorMessage(""); // Clear error message
        setShowModal(false);
      } catch (error) {
        console.error("Failed to create knowledge base:", error);
        setErrorMessage("Failed to create knowledge base. Please try again.");
      } finally {
        setIsSaving(false); // Hide spinner after saving
      }
    }
  };

  const handleRename = () => {
    if (inputValue.trim()) {
      const updatedFiles = fileNames.map((file) =>
        file.name === selectedFile.name
          ? { ...file, name: inputValue.trim() }
          : file
      );
      setFileNames(updatedFiles);
      setSelectedFile({ ...selectedFile, name: inputValue.trim() });
      setInputValue("");
      setShowModal(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedFile._id) {
        // Call the API to delete the selected knowledge base if it exists in the database
        await authService.deleteKnowledgeBase(selectedFile._id, token);
      }

      // Remove the deleted file from the state
      const updatedFiles = fileNames.filter(
        (file) => file.name !== selectedFile.name
      );
      setFileNames(updatedFiles);

      // Update the selected file or reset to an empty state if no files remain
      if (updatedFiles.length > 0) {
        setSelectedFile(updatedFiles[0]);
        setWebUrls(updatedFiles[0].webUrls || []);
        setTextFiles(updatedFiles[0].textFiles || []);
        setUploadedFiles(updatedFiles[0].uploadedFiles || []);
      } else {
        setFileNames([]);
        setSelectedFile(null);
        setWebUrls([]);
        setTextFiles([]);
        setUploadedFiles([]);
      }
    } catch (error) {
      console.error("Failed to delete knowledge base:", error);
    }
  };

  // Refetch knowledge bases when returning to the Knowledge Base page
  useEffect(() => {
    if (location.pathname === "/agent_settings/knowledge_base") {
      fetchKnowledgeBases(); // Refetch knowledge bases
    }
  }, [location.pathname, fetchKnowledgeBases]); // Add location.pathname and fetchKnowledgeBases as dependencies

  const handleFileClick = (file) => {
    if (selectedFile && selectedFile.name === file.name) {
      // Prevent resetting state if the same file is clicked
      return;
    }
    setSelectedFile(file);
    // Update the content displayed in the main-content div
    const fileContent = {
      webUrls: file.webUrls || [],
      textFiles: file.textFiles || [],
      uploadedFiles: file.uploadedFiles || [],
    };
    setWebUrls(fileContent.webUrls);
    setTextFiles(fileContent.textFiles);
    setUploadedFiles(fileContent.uploadedFiles);
  };

  const handleMouseEnter = (file) => {
    setHoveredFile(file);
  };

  const handleMouseLeave = () => {
    setHoveredFile(null);
  };

  const handleSaveUrl = async (url) => {
    if (webUrls.some((existingUrl) => existingUrl.url === url)) {
      return true; // Indicate that the URL already exists
    }
    if (editUrlIndex !== null) {
      // Editing an existing URL
      const updatedUrls = [...webUrls];
      updatedUrls[editUrlIndex] = { ...updatedUrls[editUrlIndex], url };
      setWebUrls(updatedUrls);
      setEditUrlIndex(null);
    } else {
      // Adding a new URL
      try {
        const itemData = {
          type: "web_page",
          name: url,
          content: { url },
        };
        const response = await authService.addKnowledgeBaseItem(
          selectedFile._id,
          itemData,
          token
        );
        const newUrl = {
          _id: response._id,
          url: response.content.url,
        };
        setWebUrls([...webUrls, newUrl]);
      } catch (error) {
        console.error("Failed to save web page link:", error);
      }
    }
    return false; // Indicate that the URL was successfully added
  };

  const handleSaveText = async (textFile) => {
    if (editTextIndex !== null) {
      // Editing an existing text file
      if (
        textFiles.some(
          (existingFile, index) =>
            existingFile.name === textFile.name && index !== editTextIndex
        )
      ) {
        console.error("Duplicate file name not allowed.");
        return;
      }
      const updatedTextFiles = [...textFiles];
      updatedTextFiles[editTextIndex] = textFile;
      setTextFiles(updatedTextFiles);
      setEditTextIndex(null);
    } else {
      // Adding a new text file
      if (
        textFiles.some((existingFile) => existingFile.name === textFile.name)
      ) {
        console.error("Duplicate file name not allowed.");
        return;
      }
      try {
        const itemData = {
          type: "text",
          name: textFile.name,
          content: { text: textFile.content },
        };
        const response = await authService.addKnowledgeBaseItem(
          selectedFile._id,
          itemData,
          token
        );
        const savedText = {
          name: response.name,
          content: response.content.text,
        };
        setTextFiles([...textFiles, savedText]);
      } catch (error) {
        console.error("Failed to save text file:", error);
      }
    }
  };

  const handleSaveFile = async (files) => {
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const response = await authService.uploadKnowledgeBaseFile(
          selectedFile._id,
          file,
          token
        ); // Upload file to the knowledge base
        const uploadedFile = {
          name: response.data.name,
          size: (response.data.content.fileSize / 1024 / 1024).toFixed(1), // Convert bytes to MB
          azureUrl: response.data.content.azureUrl,
        };
        uploadedFiles.push(uploadedFile);
      }
      setUploadedFiles((prevFiles) => [...prevFiles, ...uploadedFiles]); // Correctly merge new files with existing ones
    } catch (error) {
      console.error("Failed to upload files:", error);
    }
  };

  const fetchKnowledgeBaseItems = async () => {
    try {
      setIsContentLoading(true); // Show spinner while fetching items
      const items = await authService.getKnowledgeBaseItems(
        selectedFile._id,
        token
      ); // Fetch items for the selected knowledge base
      const webUrls = items
        .filter((item) => item.type === "web_page")
        .map((item) => ({
          _id: item._id, // Include the item ID
          url: item.content.url,
          sync: item.sync,
        }));
      const textFiles = items
        .filter((item) => item.type === "text")
        .map((item) => ({
          _id: item._id, // Include the item ID
          name: item.name,
          sync: item.sync,
          content: item.content.text,
        }));
      const uploadedFiles = items
        .filter((item) => item.type === "file")
        .map((item) => ({
          _id: item._id, // Include the item ID
          name: item.name,
          size: (item.content.fileSize / 1024 / 1024).toFixed(1), // Convert bytes to MB
          azureUrl: item.content.azureUrl,
          sync: item.sync,
        }));
      setWebUrls(webUrls);
      setTextFiles(textFiles);
      setUploadedFiles(uploadedFiles);
    } catch (error) {
      console.error("Failed to fetch knowledge base items:", error);
    } finally {
      setIsContentLoading(false); // Hide spinner after fetching items
    }
  };

  useEffect(() => {
    if (selectedFile && selectedFile._id) {
      fetchKnowledgeBaseItems(); // Fetch items whenever the selected file changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  const handleEditUrl = (index) => {
    setEditUrlIndex(index);
  };

  const handleEditText = (index) => {
    setEditTextIndex(index);
  };

  const handleDeleteUrl = (index) => {
    setDeleteIndex(index);
    setDeleteType("url");
    setShowDeleteModal(true);
  };

  const handleDeleteText = (index) => {
    setDeleteIndex(index);
    setDeleteType("text");
    setShowDeleteModal(true);
  };

  const handleDeleteFile = (index) => {
    setDeleteIndex(index);
    setDeleteType("file");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true); // Show spinner
      if (deleteType === "url") {
        const itemId = webUrls[deleteIndex]._id; // Use the correct item ID
        await authService.deleteKnowledgeBaseItem(
          selectedFile._id,
          itemId,
          token
        ); // Call API to delete the item
        const updatedUrls = webUrls.filter((_, i) => i !== deleteIndex);
        setWebUrls(updatedUrls);
      } else if (deleteType === "text") {
        const itemId = textFiles[deleteIndex]._id; // Use the correct item ID
        await authService.deleteKnowledgeBaseItem(
          selectedFile._id,
          itemId,
          token
        ); // Call API to delete the item
        const updatedTextFiles = textFiles.filter((_, i) => i !== deleteIndex);
        setTextFiles(updatedTextFiles);
      } else if (deleteType === "file") {
        const itemId = uploadedFiles[deleteIndex]._id; // Use the correct item ID
        await authService.deleteKnowledgeBaseItem(
          selectedFile._id,
          itemId,
          token
        ); // Call API to delete the item
        const updatedFiles = uploadedFiles.filter((_, i) => i !== deleteIndex);
        setUploadedFiles(updatedFiles);
      }
      setShowDeleteModal(false);
      setDeleteIndex(null);
      setDeleteType("");
    } catch (error) {
      console.error("Failed to delete item:", error);
    } finally {
      setIsDeleting(false); // Hide spinner
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteIndex(null);
    setDeleteType("");
  };

  const handleSync = async () => {
    if (!selectedFile || !selectedFile._id) {
      console.error("No knowledge base selected for syncing.");
      return;
    }

    setIsSyncing(true); // Show syncing indicator
    try {
      const response = await authService.syncKnowledgeBase(selectedFile._id, token);

      if (response) {
        fetchKnowledgeBaseItems();
        console.log("Sync successful:");
      } else {
        console.error("Failed to sync knowledge base:", response.statusText);
      }
    } catch (error) {
      console.error("Error syncing knowledge base:", error);
    } finally {
      setIsSyncing(false); // Hide syncing indicator
    }
  };

  return (
    <div className="d-flex">
      <LeftNav />
      <Container
        fluid
        className="instructions-content flex-grow-1 p-4 bg-light"
      >
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <Row>
              <Col>
                <h2 className="fs-five-title fw-bold">Agent Settings</h2>
                <p className="gray-color">
                  Customize Nouraa to enhance your business call experience.
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card className="p-4 mt-4 border-0">
                  <div>
                    <Link
                      to="/agent_settings"
                      className="text-decoration-none purple-color fw-bold mb-4"
                      onClick={() => navigate("/agent_settings")}
                    >
                      <img
                        src="../assets/arrow-left.svg"
                        alt="Arrow Right"
                        className="me-1"
                      />
                      Go Back
                    </Link>
                  </div>
                  <hr className="mb-4 border-dark-subtle" />
                  <div className="d-flex gap-3">
                    {/* Sidebar */}
                    <div
                      className={`sidebar card border w-25 ${
                        fileNames.length > 13 ? "" : "vh-100"
                      }`}
                    >
                      <div className="d-flex justify-content-between align-items-center border-bottom">
                        <div className="p-2">
                          <span className="fw-bold text-nowrap">
                            <img
                              src="../assets/fluent_book-default-28-regular.svg"
                              alt="Knowledge Base"
                              className="nav-icon"
                            />{" "}
                            Knowledge Base
                          </span>
                        </div>
                        <div className="p-2">
                          <button className="btn" onClick={handleShowModal}>
                            <img
                              src="/assets/plus-blue.svg"
                              alt="Info"
                              className="bg-icon rounded-1"
                            />
                          </button>
                        </div>
                      </div>
                      <div className="py-3">
                        {fileNames.map((file, index) => (
                          <div
                            key={index}
                            className={` file-name-btn d-flex justify-content-between align-items-center p-3 rounded-3 ${
                              selectedFile && selectedFile.name === file.name
                                ? "active bg-light fw-bold "
                                : ""
                            }`}
                            onClick={() => handleFileClick(file)}
                            onMouseEnter={() => handleMouseEnter(file)}
                            onMouseLeave={handleMouseLeave}
                            style={{ cursor: "pointer" }}
                          >
                            <span>{file.name}</span>
                            {hoveredFile === file && (
                              <EditDeleteButton
                                onRename={handleShowRenameModal}
                                onDelete={handleDelete}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="main-content border card w-75">
                      {isContentLoading ? (
                        <div className="d-flex justify-content-center align-items-center ">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </Spinner>
                        </div>
                      ) : fileNames.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-center flex-grow-1">
                          <div className="text-center add-btn-container">
                            <div className="empty-icon display-4 text-muted">
                              {" "}
                              <img
                                src="../assets/material-symbols-light_id-card-outline.svg"
                                alt="Knowledge Base"
                                className="nav-icon"
                              />
                            </div>
                            <p className="gray-color">
                              You don't have any files added yet. Let's add them
                              now!
                            </p>
                            <Button
                              variant="none"
                              className="px-5 py-2 fw-bold btn-group purple-color"
                              onClick={handleShowModal}
                            >
                              <img
                                src="/assets/plus-blue.svg"
                                alt="Info"
                                className="me-2"
                              />{" "}
                              Add
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="border-bottom main-content-padding w-100 d-flex justify-content-between align-items-center">
                            <div>
                              <span className="fs-three fw-bold">
                                {selectedFile
                                  ? selectedFile.name
                                  : fileNames[0].name}
                              </span>
                              <br />
                              <small className="text-muted">
                                Last updated on:{" "}
                                {selectedFile
                                  ? selectedFile.timestamp
                                  : fileNames[0].timestamp}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <UploadButton
                                onSaveUrl={handleSaveUrl}
                                onSaveText={handleSaveText}
                                onSaveFile={handleSaveFile}
                                editUrl={webUrls[editUrlIndex]}
                                editTextFile={textFiles[editTextIndex]}
                                editFile={uploadedFiles[editFileIndex]}
                                setEditUrlIndex={setEditUrlIndex}
                                setEditTextIndex={setEditTextIndex}
                                setEditFileIndex={setEditFileIndex}
                              />
                              <button
                                type="button"
                                className="btn btn-primary align-items-center"
                                onClick={handleSync}
                                disabled={isSyncing}
                              >
                                {isSyncing ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                    />
                                    Syncing...
                                  </>
                                ) : (
                                  <>
                                    <img
                                      src="/assets/sync.svg"
                                      alt="sync"
                                      color="#fff"
                                      style={{ width: "25px", height: "22px" }}
                                    />
                                    <span>Sync</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          <div className="p-4 pb-3 w-100">
                            <ul className="list-group">
                              {webUrls.map((urlObj, index) => (
                                <li
                                  key={urlObj._id}
                                  className="py-3 bg-light-subtle d-flex justify-content-between list-group-item"
                                >
                                  <div className="fw-bold gray-color d-flex w-75">
                                    <img
                                      src="/assets/link.svg"
                                      alt="Info"
                                      className="me-2 bg-icon"
                                    />
                                    <span className="text-break">
                                      {urlObj.url}
                                    </span>
                                    {urlObj.sync ? (
                                      <img
                                        src="../assets/check-sign-2.svg"
                                        alt="check icon"
                                        className="ms-2"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                    ) : (
                                      <img
                                        src="../assets/cross-sign.svg"
                                        alt="cross sign"
                                        className="ms-2"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                    )}
                                    {/* Render only the `url` property */}
                                  </div>
                                  <div>
                                    <img
                                      src="../assets/edit-2.svg"
                                      alt="Knowledge Base"
                                      className="me-2"
                                      title="Edit"
                                      onClick={() => handleEditUrl(index)}
                                      style={{ cursor: "pointer" }}
                                    />{" "}
                                    <img
                                      src="../assets/trash-1.svg"
                                      alt="Knowledge Base"
                                      className="me-2"
                                      title="Delete"
                                      onClick={() => handleDeleteUrl(index)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="px-4 pb-3 w-100">
                            <ul className="list-group">
                              {uploadedFiles.map((file, index) => (
                                <li
                                  key={index}
                                  className="py-3 bg-light-subtle d-flex justify-content-between list-group-item"
                                >
                                  <div className="d-flex align-items-center">
                                    <img
                                      src="../assets/file-text-pdf.svg"
                                      alt="Knowledge Base"
                                      className="bg-icon me-2"
                                    />
                                    <div>
                                      <strong className="fw-bold gray-color">
                                        {file.name}{" "}
                                        {file.sync ? (
                                          <img
                                            src="../assets/check-sign-2.svg"
                                            alt="check icon"
                                            className="ms-1"
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                            }}
                                          />
                                        ) : (
                                          <img
                                            src="../assets/cross-sign.svg"
                                            alt="cross sign"
                                            className="ms-1"
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                            }}
                                          />
                                        )}
                                      </strong>
                                      <div className="text-muted small">
                                        {file.size} MB • Uploaded
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <img
                                      src="../assets/trash-1.svg"
                                      alt="Delete"
                                      className="me-2"
                                      title="Delete"
                                      onClick={() => handleDeleteFile(index)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="px-4 pb-3 w-100">
                            <ul className="list-group">
                              {textFiles.map((file, index) => (
                                <li
                                  key={index}
                                  className="py-3 bg-light-subtle d-flex justify-content-between list-group-item"
                                >
                                  <div className="d-flex align-items-center fw-bold gray-color w-75">
                                    <img
                                      src="../assets/type.svg"
                                      alt="Knowledge Base"
                                      className="bg-icon me-2"
                                    />
                                    <div className="d-flex flex-column">
                                      <strong className="me-2">
                                        {file.name}:
                                      </strong>
                                      <div className="text-break">
                                        {file.content}
                                      </div>
                                    </div>
                                    {file.sync ? (
                                      <img
                                        src="../assets/check-sign-2.svg"
                                        alt="check icon"
                                        className="ms-3"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                    ) : (
                                      <img
                                        src="../assets/cross-sign.svg"
                                        alt="cross sign"
                                        className="ms-3"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div>
                                    <img
                                      src="../assets/edit-2.svg"
                                      alt="Knowledge Base"
                                      className="me-2"
                                      title="Edit"
                                      onClick={() => handleEditText(index)}
                                      style={{ cursor: "pointer" }}
                                    />{" "}
                                    <img
                                      src="../assets/trash-1.svg"
                                      alt="Knowledge Base"
                                      className="me-2"
                                      title="Delete"
                                      onClick={() => handleDeleteText(index)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {webUrls.length === 0 &&
                            textFiles.length === 0 &&
                            uploadedFiles.length === 0 && (
                              <div className="d-flex justify-content-center align-items-center flex-grow-1">
                                <div className="text-center upload-btn-container second-upload-btn">
                                  <div className="empty-icon display-4 text-muted">
                                    {" "}
                                    <img
                                      src="../assets/line-md_file.svg"
                                      alt="Knowledge Base"
                                      className="nav-icon"
                                    />
                                  </div>
                                  <p className="gray-color">
                                    You don't have any documents added to this
                                    file yet. Let’s upload them now!
                                  </p>
                                  <UploadButton
                                    onSaveUrl={handleSaveUrl}
                                    onSaveText={handleSaveText}
                                    onSaveFile={handleSaveFile}
                                    setEditUrlIndex={setEditUrlIndex}
                                    setEditTextIndex={setEditTextIndex}
                                    setEditFileIndex={setEditFileIndex}
                                  />
                                </div>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>

      {/* Add/Rename Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            isRenaming ? handleRename() : handleSave();
          }
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-three">
            {isRenaming ? "Rename Knowledge Base" : "Add Knowledge Base"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="py-2 pb-5">
            <Form.Group controlId="formBasicEmail">
              <Form.Label>
                {isRenaming ? "New file name" : "File name"}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={
                  isRenaming ? "Enter new file name" : "Enter file name"
                }
                value={inputValue}
                onChange={handleInputChange}
                isInvalid={!!errorMessage} // Highlight input if there's an error
              />

              <Form.Control.Feedback type="invalid">
                {errorMessage}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="none"
            className="fw-bold btn btn-light"
            onClick={handleCloseModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="ms-2 bg-purple-color fw-bold text-white btn btn-none"
            onClick={isRenaming ? handleRename : handleSave}
            disabled={isSaving} // Disable button while saving
          >
            {isSaving ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : isRenaming ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-three fw-bold">
            Delete Knowledge Base
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-4">
            {errorMessage ? (
              <p className="text-danger">{errorMessage}</p>
            ) : (
              <p>
                Are you sure you want to delete this item? This action cannot be
                undone.
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="none"
            className="fw-bold btn btn-light"
            onClick={handleCloseDeleteModal}
            disabled={isDeleting} // Disable cancel button while deleting
          >
            Cancel
          </Button>
          {!errorMessage && (
            <Button
              variant="danger"
              className="ms-2 fw-bold text-white btn btn-none"
              onClick={handleConfirmDelete}
              disabled={isDeleting} // Disable delete button while deleting
            >
              {isDeleting ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KnowledgeBase;
